// Crew (Friends List) Service for Pops Culture
import { db } from './firebase';
import { doc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { getCurrentProfile } from './authService';

export type CrewRelation = 'self' | 'in_crew' | 'request_sent' | 'request_received' | 'none';

/**
 * Determine the friendship / Crew relationship between current user and target user
 */
export function getCrewRelation(targetUid: string): CrewRelation {
  const current = getCurrentProfile();
  if (!current || !current.uid) return 'none';
  if (current.uid === targetUid) return 'self';

  const crew = current.crew || [];
  if (crew.includes(targetUid)) return 'in_crew';

  const sent = current.crewRequestsSent || [];
  if (sent.includes(targetUid)) return 'request_sent';

  const received = current.crewRequestsReceived || [];
  if (received.includes(targetUid)) return 'request_received';

  return 'none';
}

/**
 * Send a Crew friend request to another dad
 */
export async function sendCrewRequest(targetUid: string): Promise<void> {
  const current = getCurrentProfile();
  if (!current || !current.uid) throw new Error('Must be logged in to friend a dad');
  if (current.uid === targetUid) return;

  const currentRef = doc(db, 'users', current.uid);
  const targetRef = doc(db, 'users', targetUid);

  // Update current user's sent requests
  await updateDoc(currentRef, {
    crewRequestsSent: arrayUnion(targetUid)
  });

  // Update target user's received requests
  await updateDoc(targetRef, {
    crewRequestsReceived: arrayUnion(current.uid)
  });
}

/**
 * Accept an incoming Crew friend request -> both dads are now in each other's Crew list!
 */
export async function acceptCrewRequest(requesterUid: string): Promise<void> {
  const current = getCurrentProfile();
  if (!current || !current.uid) throw new Error('Must be logged in to accept friend request');

  const currentRef = doc(db, 'users', current.uid);
  const requesterRef = doc(db, 'users', requesterUid);

  // Add requester to current user's crew and clean up received request
  await updateDoc(currentRef, {
    crew: arrayUnion(requesterUid),
    crewRequestsReceived: arrayRemove(requesterUid),
    crewCount: increment(1)
  });

  // Add current user to requester's crew and clean up sent request
  await updateDoc(requesterRef, {
    crew: arrayUnion(current.uid),
    crewRequestsSent: arrayRemove(current.uid),
    crewCount: increment(1)
  });
}

/**
 * Decline an incoming Crew friend request
 */
export async function declineCrewRequest(requesterUid: string): Promise<void> {
  const current = getCurrentProfile();
  if (!current || !current.uid) throw new Error('Must be logged in');

  const currentRef = doc(db, 'users', current.uid);
  const requesterRef = doc(db, 'users', requesterUid);

  await updateDoc(currentRef, {
    crewRequestsReceived: arrayRemove(requesterUid)
  });

  await updateDoc(requesterRef, {
    crewRequestsSent: arrayRemove(current.uid)
  });
}

/**
 * Cancel a pending outgoing Crew request
 */
export async function cancelCrewRequest(targetUid: string): Promise<void> {
  const current = getCurrentProfile();
  if (!current || !current.uid) throw new Error('Must be logged in');

  const currentRef = doc(db, 'users', current.uid);
  const targetRef = doc(db, 'users', targetUid);

  await updateDoc(currentRef, {
    crewRequestsSent: arrayRemove(targetUid)
  });

  await updateDoc(targetRef, {
    crewRequestsReceived: arrayRemove(current.uid)
  });
}

/**
 * Remove a dad from your Crew list (unfriends both ways)
 */
export async function removeFromCrew(targetUid: string): Promise<void> {
  const current = getCurrentProfile();
  if (!current || !current.uid) throw new Error('Must be logged in');

  const currentRef = doc(db, 'users', current.uid);
  const targetRef = doc(db, 'users', targetUid);

  await updateDoc(currentRef, {
    crew: arrayRemove(targetUid),
    crewCount: increment(-1)
  });

  await updateDoc(targetRef, {
    crew: arrayRemove(current.uid),
    crewCount: increment(-1)
  });
}
